import { useTurnstile } from "../TurnstileContext";
import { Button, Input, Message } from "../base-ui/index";
import { getSSOCallbackRedirectTo } from "../sso-redirect";
import { trackAuthFormSubmit } from "../trackAuthFormSubmit";
import { ViewType } from "../types";
import { safeAsync } from "@/lib/utils";
import { I18nVariables, RedirectTo, VIEWS } from "@supabase/auth-ui-shared";
import { SupabaseClient } from "@supabase/supabase-js";
import { ArrowLeft } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useState } from "react";
import { useCookies } from "react-cookie";

function SSO({
  setAuthView,
  supabaseClient,
  showLinks = false,
}: {
  setAuthView: (view: ViewType) => void;
  supabaseClient: SupabaseClient;
  redirectTo?: RedirectTo;
  i18n?: I18nVariables;
  showLinks?: boolean;
}) {
  const COOKIE_NAME_DOMAIN = "sso_domain";
  const [cookies, setCookie] = useCookies([COOKIE_NAME_DOMAIN]);
  const searchParams = useSearchParams();
  // /auth/callback sends users here with their email when it blocks a non-SSO login on an
  // SSO domain, because only the browser client can start a completable SSO flow.
  const ssoRequired = searchParams.get("sso_required") === "1";
  const [domainOrEmail, setDomainOrEmail] = useState<string>(
    searchParams.get("email") ?? (cookies[COOKIE_NAME_DOMAIN] as string) ?? ""
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { getToken } = useTurnstile();

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    setDomainOrEmail(e.target.value.trim());
  };

  const handleSSOSignIn = async () => {
    if (!domainOrEmail) {
      setError("Please enter a domain");
      return;
    }

    const domain = domainOrEmail.includes("@")
      ? domainOrEmail.split("@")[1]
      : domainOrEmail;
    setLoading(true);
    trackAuthFormSubmit({ input: domainOrEmail, action: "sso" });
    const captchaToken = await getToken();
    const { data, error: ssoError } = await supabaseClient.auth.signInWithSSO({
      domain,
      options: {
        captchaToken,
        redirectTo: getSSOCallbackRedirectTo(searchParams.get("deeplink")),
      },
    });
    if (ssoError || !data?.url) {
      setLoading(false);
      setError(ssoError?.message ?? "Could not start single sign-on");
      return;
    }
    // Keep loading until the browser navigates to the IdP.
    setCookie(COOKIE_NAME_DOMAIN, domain, { path: "/" });
    router.push(data.url);
  };

  return (
    <div className="flex flex-col gap-3 text-left">
      {ssoRequired && (
        <p className="text-center text-sm text-content-tertiary">
          Your organization uses single sign-on. Continue with SSO to finish
          signing in.
        </p>
      )}
      <Input
        id="domain"
        name="domain"
        type="text"
        autoFocus
        autoComplete="off"
        // Not a credential field: keep password-manager overlays away.
        data-1p-ignore=""
        data-lpignore="true"
        data-bwignore="true"
        aria-label="Company email or domain"
        value={domainOrEmail || ""}
        placeholder="Company email or domain"
        onChange={handleDomainChange}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter") {
            e.preventDefault();
            safeAsync(handleSSOSignIn)();
          }
        }}
      />
      <Button
        color="primary"
        type="submit"
        loading={loading}
        onClick={safeAsync(handleSSOSignIn)}
      >
        {loading ? "Loading" : "Sign in with SSO"}
      </Button>
      {showLinks && (
        <a
          href="#auth-sign-in"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            setAuthView(VIEWS.SIGN_IN);
          }}
          className="mt-3 inline-flex items-center justify-center gap-1 text-sm text-blue-500 hover:text-blue-600"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </a>
      )}
      {error && <Message color="danger">{error}</Message>}
    </div>
  );
}

export { SSO };
